import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetLatestAuthQuery } from "../store/services/authService";
import { isAdminSrmanager, navItems } from "../utils/menuItems";
import { checkPermission } from "../utils/commonMethods";
import { useUserPermission } from "../hooks/user";
import Logo from "../assets/LogoDark.svg";
import {
  LayoutGrid,
  Folder,
  Star,
  ClipboardClock,
  Phone,
  Users as UsersGroup,
  Calendar,
  User,
  BadgeCheck,
  Building,
  Sparkles,
  Users,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ClipboardList,
  MessageSquare,
  CalendarCheck,
  Briefcase,
  Clock,
  CalendarX,
  FileText,
  Home,
  DollarSign,
  TrendingUp,
  Receipt,
  Gift,
  FileSpreadsheet,
  FileChartColumn,
  FileChartLine,
  TableProperties,
  FileUser,
} from "lucide-react";

export default function PermanentSidebar({ open, onToggle, width }) {
  const navigate = useNavigate();
  const { auth } = useSelector((state) => state.auth);
  const perm = useUserPermission(auth?._id);
  const { data: user } = useGetLatestAuthQuery(auth?._id);
  const { pathname } = useLocation();
  const isCollapsed = !open;

  const [isHrmsOpen, setIsHrmsOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isPayrollOpen, setIsPayrollOpen] = useState(false);
  const [isAccountsOpen, setIsAccountsOpen] = useState(false);

  // Helper to determine active page for highlighting
  const [currentPage, setCurrentPage] = useState("");

  useEffect(() => {
    if (pathname === "/dashboard") setCurrentPage("dashboard");
    else if (pathname.includes("/leaderboard")) setCurrentPage("leaderboard");
    else if (pathname.includes("/projects")) setCurrentPage("projects");
    else if (pathname.includes("/leads")) setCurrentPage("leads");
    // else if (pathname.includes("/reports")) setCurrentPage("reports");
    // Reports
    else if (pathname.includes("/reports/calllog-report"))
      setCurrentPage("calllog-report");
    else if (pathname.includes("/reports/meeting-report"))
      setCurrentPage("meeting-report");
    else if (pathname.includes("/reports/project-report"))
      setCurrentPage("project-report");
    else if (pathname.includes("/reports/current-assignee-report"))
      setCurrentPage("current-assignee-report");
    else if (pathname.includes("/reports/re-assignee-report"))
      setCurrentPage("re-assignee-report");
    else if (pathname.includes("/reports/user-report"))
      setCurrentPage("user-report");
    else if (pathname.includes("/reminders")) setCurrentPage("reminders");
    else if (pathname.includes("/callingData")) setCurrentPage("calling-data");
    else if (pathname.includes("/meetings")) setCurrentPage("meetings");
    else if (pathname.includes("/bookings")) setCurrentPage("bookings");
    else if (pathname === "/users") setCurrentPage("users");
    else if (pathname.includes("/users/teams")) setCurrentPage("teams");
    else if (pathname.includes("/rsvp")) setCurrentPage("rsvp");
    else if (pathname.includes("/developers")) setCurrentPage("developers");
    else if (pathname.includes("/highlights")) setCurrentPage("highlights");
    // HRMS
    else if (pathname.includes("/HrManagement/dashboard"))
      setCurrentPage("hrms-dashboard");
    else if (pathname.includes("/HrManagement/users"))
      setCurrentPage("employees");
    else if (pathname.includes("/HrManagement/attendance"))
      setCurrentPage("attendance");
    else if (pathname.includes("/HrManagement/leaves")) setCurrentPage("leave");
    else if (pathname.includes("/HrManagement/interview"))
      setCurrentPage("interview");
    // Payroll
    else if (pathname.includes("/HrManagement/payroll/department"))
      setCurrentPage("departments");
    else if (pathname.includes("/HrManagement/shift")) setCurrentPage("shifts");
    else if (pathname.includes("/HrManagement/offDays"))
      setCurrentPage("off-days");
    else if (pathname.includes("/HrManagement/allowance"))
      setCurrentPage("allowances");
    else if (pathname.includes("/HrManagement/holidays"))
      setCurrentPage("public-holidays");
    else if (pathname.includes("/HrManagement/salary-slips"))
      setCurrentPage("salaries");
    // Accounts
    else if (pathname.includes("/expense/expense-list"))
      setCurrentPage("expenses");
    else if (pathname.includes("/invoice")) setCurrentPage("invoice");
    else if (pathname.includes("/incentive")) setCurrentPage("incentive");
    else setCurrentPage("");
  }, [pathname]);

  // Navigate handler
  const onNavigate = (key) => {
    switch (key) {
      case "dashboard":
        navigate("/dashboard");
        break;
      case "leaderboard":
        navigate("/leaderboard");
        break;
      case "projects":
        navigate("/projects");
        break;
      case "calllog-report":
        navigate("/reports/calllog-report");
        break;
      case "meeting-report":
        navigate("/reports/meeting-report");
        break;
      case "project-report":
        navigate("/reports/project-report");
        break;
      case "current-assignee-report":
        navigate("/reports/current-assignee-report");
        break;
      case "re-assignee-report":
        navigate("/reports/re-assignee-report");
        break;
      case "user-report":
        navigate("/reports/user-report");
        break;
      case "reminders":
        navigate("/reminders");
        break;
      case "leads":
        navigate("/leads?type=lead");
        break;
      case "calling-data":
        navigate("/callingData?type=calling_data");
        break;
      case "meetings":
        navigate("/meetings");
        break;
      case "bookings":
        navigate("/bookings");
        break;
      case "users":
        navigate("/users");
        break;
      case "teams":
        navigate("/users/teams");
        break;
      case "rsvp":
        navigate("/rsvp/manager");
        break;
      case "developers":
        navigate("/developers");
        break;
      case "highlights":
        navigate("/highlights");
        break;
      // HRMS
      case "hrms-dashboard":
        navigate("/HrManagement/dashboard");
        break;
      case "employees":
        navigate("/HrManagement/users");
        break;
      case "attendance":
        navigate(
          auth?.role === "agent"
            ? `/HrManagement/individualAtt/${auth?._id}`
            : "/HrManagement/attendance",
        );
        break;
      case "leave":
        navigate("/HrManagement/leaves");
        break;
      case "interview":
        navigate("/HrManagement/interview");
        break;
      // Payroll
      case "departments":
        navigate("/HrManagement/payroll/department");
        break;
      case "shifts":
        navigate("/HrManagement/shift");
        break;
      case "off-days":
        navigate("/HrManagement/offDays");
        break;
      case "allowances":
        navigate("/HrManagement/allowance");
        break;
      case "public-holidays":
        navigate("/HrManagement/holidays");
        break;
      case "salaries":
        navigate("/HrManagement/salary-slips");
        break;
      // Accounts
      case "expenses":
        navigate("/expense/expense-list");
        break;
      case "invoice":
        navigate("/invoice");
        break;
      case "incentive":
        navigate("/incentive");
        break;
      default:
        break;
    }
  };

  // Permission Logic Helpers
  const canShowItem = (itemTitle, module, task) => {
    // Legacy logic from original file
    if (
      auth?.role === "agent" &&
      (itemTitle === "Users" || itemTitle === "Teams")
    )
      return false;
    if (
      (auth?.role === "sup_admin" ||
        auth?.role === "sub_admin" ||
        auth?.role === "sr_manager") &&
      itemTitle === "Teams"
    )
      return false;

    if (
      (auth?.role === "manager" ||
        auth?.role === "assistant_manager" ||
        auth?.role === "team_lead") &&
      itemTitle === "Users"
    )
      return false;

    if (
      auth?.role !== "sup_admin" &&
      auth?.role !== "sub_admin" &&
      auth?.role !== "sr_manager" &&
      itemTitle === "Projects"
    )
      return false;

    if (
      auth?.role !== "sup_admin" &&
      auth?.role !== "sub_admin" &&
      (itemTitle === "Developers" || itemTitle === "Accounts")
    )
      return false;

    if (
      !isAdminSrmanager.includes(auth?.role) &&
      user?.status !== "approved" &&
      itemTitle === "HRMS"
    )
      return false;

    // Check permission module/task
    if (module && task) {
      return checkPermission(perm, module, task, auth?.role);
    }
    return true;
  };

  return (
    <aside
      className="flex-shrink-0 z-50 bg-gradient-to-b from-white via-white to-[#f9fbfd]/30 border-r border-gray-200/80 flex flex-col transition-all duration-300 shadow-xl shadow-gray-200/20"
      style={{
        height: "100vh",
        position: "fixed",
        top: 0,
        left: 0,
        width,
        minWidth: width,
        maxWidth: width,
        boxSizing: "border-box",
      }}
    >
      {/* Logo - Fixed height with gradient background */}
      <div className="h-16 flex items-center justify-between px-6 border-b border-gray-100 bg-gradient-to-r from-[#f9fbfd]/50 to-white relative overflow-hidden">
        {/* Decorative gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#2e67be]/5 via-transparent to-[#4985f2]/5 opacity-50"></div>

        {!isCollapsed && (
          <Link to={"/dashboard"}>
            <img
              src={Logo}
              alt="ZELLIX"
              className="h-10 w-auto object-cover relative z-10 drop-shadow-sm"
            />
          </Link>
        )}
        <button
          onClick={onToggle}
          className={`${isCollapsed ? "mx-auto" : "ml-auto"
            } p-2 text-gray-400 hover:text-[#2e67be] hover:bg-white rounded-xl transition-all duration-200 active:scale-95 cursor-pointer relative z-10 shadow-sm hover:shadow-md border border-transparent hover:border-[#2e67be]/10`}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation - Scrollable */}
      <nav className="flex-1 overflow-y-auto px-4 py-6 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent hover:scrollbar-thumb-gray-300">
        <ul className="space-y-1.5">
          {/* Dashboard */}
          {canShowItem("Dashboard", "Dashboard", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("dashboard")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "dashboard"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Dashboard" : ""}
              >
                {currentPage === "dashboard" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <LayoutGrid
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "dashboard"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Dashboard
                  </span>
                )}
                {currentPage === "dashboard" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {[
            "sup_admin",
            "sub_admin",
            "sr_manager",
            "manager",
            "assistant_manager",
            "team_lead",
            "agent",
          ].includes(auth?.role) && (
              <li>
                <button
                  onClick={() => onNavigate("leaderboard")}
                  className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"} py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "leaderboard" ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20" : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"}`}
                  title={isCollapsed ? "Leaderboard" : ""}
                >
                  <TrendingUp
                    className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "leaderboard" ? "drop-shadow-sm" : "group-hover:scale-110 group-hover:rotate-3"}`}
                    strokeWidth={2.5}
                  />
                  {!isCollapsed && (
                    <span className="text-[15px] font-medium relative z-10">
                      Leaderboard
                    </span>
                  )}
                  {currentPage === "leaderboard" && !isCollapsed && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />
                  )}
                </button>
              </li>
            )}

          {["sup_admin", "sub_admin", "developer"].includes(auth?.role) && <li><Link to="/projector/control" title="Projector control" className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"} py-3.5 rounded-xl text-gray-600 hover:bg-blue-50`}><Sparkles className="w-5 h-5" />{!isCollapsed && <span className="text-[15px] font-medium">Projector</span>}</Link></li>}
          {/* Projects */}
          {canShowItem("Projects", "Project", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("projects")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "projects"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Projects" : ""}
              >
                {currentPage === "projects" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <Folder
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "projects"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Projects
                  </span>
                )}
                {currentPage === "projects" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Reports Dropdown */}
          {
            <li>
              <button
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${[
                    "current-assignee-report",
                    "calllog-report",
                    "meeting-report",
                    "project-report",
                    "user-report",
                    "re-assignee-report",
                  ].includes(currentPage)
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                onClick={() => setIsReportOpen(!isReportOpen)}
                title={isCollapsed ? "Reports" : ""}
              >
                {[
                  "calllog-report",
                  "meeting-report",
                  "project-report",
                  "user-report",
                  "current-assignee-report",
                  "re-assignee-report",
                ].includes(currentPage) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  )}
                <FileSpreadsheet
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${[
                      "calllog-report",
                      "meeting-report",
                      "project-report",
                      "user-report",
                      "re-assignee-report",
                    ].includes(currentPage)
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Reports
                  </span>
                )}
                {!isCollapsed &&
                  (isReportOpen ? (
                    <ChevronUp
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ) : (
                    <ChevronDown
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ))}
                {["calllog-report", "meeting-report", "project-report", "user-report"].includes(
                  currentPage,
                ) &&
                  !isCollapsed && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse absolute right-4"></div>
                  )}
              </button>
              {isReportOpen && (
                <ul
                  className={`${isCollapsed ? "space-y-1 mt-1" : "ml-2 space-y-0.5 mt-1"
                    }`}
                >
                  {
                    <li>
                      <button
                        onClick={() => onNavigate("calllog-report")}
                        className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-2 px-3"
                          } py-2.5 rounded-lg transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "calllog-report"
                            ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                            : "text-gray-500 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                          }`}
                        title={isCollapsed ? "Calllog Report" : ""}
                      >
                        {currentPage === "calllog-report" && (
                          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        )}
                        <FileChartColumn
                          className={`w-4 h-4 transition-all duration-200 relative z-10 ${currentPage === "calllog-report"
                              ? "drop-shadow-sm"
                              : "group-hover:scale-110 group-hover:rotate-3"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[13px] font-medium relative z-10">
                            CallLog
                          </span>
                        )}
                        {currentPage === "calllog-report" && !isCollapsed && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                        )}
                      </button>
                    </li>
                  }
                  {["sup_admin", "sub_admin"].includes(auth?.role) && (
                    <li>
                      <button
                        onClick={() => onNavigate("project-report")}
                        className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-2 px-3"
                          } py-2.5 rounded-lg transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "project-report"
                            ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                            : "text-gray-500 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                          }`}
                        title={isCollapsed ? "Project Report" : ""}
                      >
                        <TableProperties className="w-4 h-4" strokeWidth={2.5} />
                        {!isCollapsed && <span className="text-[13px] font-medium relative z-10">Project</span>}
                        {currentPage === "project-report" && !isCollapsed && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse" />}
                      </button>
                    </li>
                  )}
                  {["sup_admin", "sub_admin","sr_manager"].includes(auth?.role) &&
                    <li>
                      <button
                        onClick={() => onNavigate("meeting-report")}
                        className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-2 px-3"
                          } py-2.5 rounded-lg transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "meeting-report"
                            ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                            : "text-gray-500 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                          }`}
                        title={isCollapsed ? "Meeting Report" : ""}
                      >
                        <FileChartColumn
                          className={`w-4 h-4 transition-all duration-200 relative z-10 ${currentPage === "meeting-report"
                              ? "drop-shadow-sm"
                              : "group-hover:scale-110 group-hover:rotate-3"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[13px] font-medium relative z-10">
                            Meeting
                          </span>
                        )}
                        {currentPage === "meeting-report" && !isCollapsed && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                        )}
                      </button>
                    </li>
                  }
                  {auth?.role !== "agent" && (
                    <li>
                      <button
                        onClick={() => onNavigate("user-report")}
                        className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-2 px-3"
                          } py-2.5 rounded-lg transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "user-report"
                            ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                            : "text-gray-500 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                          }`}
                        title={isCollapsed ? "User Report" : ""}
                      >
                        {currentPage === "user-report" && (
                          <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        )}
                        <FileUser
                          className={`w-4 h-4 transition-all duration-200 relative z-10 ${currentPage === "user-report"
                              ? "drop-shadow-sm"
                              : "group-hover:scale-110 group-hover:rotate-3"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[13px] font-medium relative z-10">
                            User
                          </span>
                        )}
                        {currentPage === "user-report" && !isCollapsed && (
                          <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                        )}
                      </button>
                    </li>
                  )}
                  {auth?.role !== "agent" && (
                    <li>
                      <button
                        onClick={() => onNavigate("current-assignee-report")}
                        className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-2 px-3"
                          } py-2.5 rounded-lg transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "current-assignee-report"
                            ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                            : "text-gray-500 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                          }`}
                        title={isCollapsed ? "Lead" : ""}
                      >
                        <FileChartLine
                          className={`w-4 h-4 transition-all duration-200 relative z-10 ${currentPage === "current-assignee-report"
                              ? "drop-shadow-sm"
                              : "group-hover:scale-110 group-hover:rotate-3"
                            }`}
                          strokeWidth={2.5}
                        />

                        {!isCollapsed && (
                          <span className="text-[13px] font-medium relative z-10">
                            Lead
                          </span>
                        )}

                        {currentPage === "current-assignee-report" &&
                          !isCollapsed && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                          )}
                      </button>
                    </li>
                  )}
                  {auth?.role !== "agent" && (
                    <li>
                      <button
                        onClick={() => onNavigate("re-assignee-report")}
                        className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-2 px-3"
                          } py-2.5 rounded-lg transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "re-assignee-report"
                            ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                            : "text-gray-500 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                          }`}
                        title={isCollapsed ? "Lead" : ""}
                      >
                        <FileChartLine
                          className={`w-4 h-4 transition-all duration-200 relative z-10 ${currentPage === "re-assignee-report"
                              ? "drop-shadow-sm"
                              : "group-hover:scale-110 group-hover:rotate-3"
                            }`}
                          strokeWidth={2.5}
                        />

                        {!isCollapsed && (
                          <span className="text-[13px] font-medium relative z-10">
                            Re Assigned
                          </span>
                        )}

                        {currentPage === "re-assignee-report" &&
                          !isCollapsed && (
                            <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                          )}
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </li>
          }

          {/* Reports */}
          {/* {
            <li>
              <button
                onClick={() => onNavigate("reports")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "reports"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Reports" : ""}
              >
                {currentPage === "reports" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <FileSpreadsheet
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "reports"
                    ? "drop-shadow-sm"
                    : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Reports
                  </span>
                )}
                {currentPage === "reports" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>} */}

          {/* Reminders */}
          <li>
            <button
              onClick={() => onNavigate("reminders")}
              className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "reminders"
                  ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                  : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                }`}
              title={isCollapsed ? "Reminders" : ""}
            >
              {currentPage === "reminders" && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              )}
              <ClipboardClock
                className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "reminders"
                    ? "drop-shadow-sm"
                    : "group-hover:scale-110 group-hover:rotate-3"
                  }`}
                strokeWidth={2.5}
              />
              {!isCollapsed && (
                <span className="text-[15px] font-medium relative z-10">
                  Reminders
                </span>
              )}
              {currentPage === "reminders" && !isCollapsed && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
              )}
            </button>
          </li>

          {/* Leads */}
          {canShowItem("Leads", "Leads", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("leads")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "leads"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Leads" : ""}
              >
                {currentPage === "leads" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <TableProperties
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "leads"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Leads
                  </span>
                )}
                {currentPage === "leads" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Calling Data */}
          {canShowItem("Calling Data", "CallingData", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("calling-data")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "calling-data"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Calling Data" : ""}
              >
                {currentPage === "calling-data" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <Phone
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "calling-data"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Calling Data
                  </span>
                )}
                {currentPage === "calling-data" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Meetings */}
          {canShowItem("Meetings", "Meeting", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("meetings")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "meetings"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Meetings" : ""}
              >
                {currentPage === "meetings" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <UsersGroup
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "meetings"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Meetings
                  </span>
                )}
                {currentPage === "meetings" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Bookings */}
          {canShowItem("Bookings", "Bookings", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("bookings")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "bookings"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Bookings" : ""}
              >
                {currentPage === "bookings" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <Calendar
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "bookings"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Bookings
                  </span>
                )}
                {currentPage === "bookings" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Users */}
          {canShowItem("Users", "Users", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("users")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "users"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Users" : ""}
              >
                {currentPage === "users" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <User
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "users"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Users
                  </span>
                )}
                {currentPage === "users" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Teams */}
          {canShowItem("Teams", "Team", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("teams")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "teams"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Teams" : ""}
              >
                {currentPage === "teams" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <UsersGroup
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "teams"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Teams
                  </span>
                )}
                {currentPage === "teams" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* RSVP */}
          {canShowItem("RSVP", "Event", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("rsvp")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "rsvp"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "RSVP" : ""}
              >
                {currentPage === "rsvp" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <BadgeCheck
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "rsvp"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Events
                  </span>
                )}
                {currentPage === "rsvp" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Developers */}
          {canShowItem("Developers", "Developer", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("developers")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "developers"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Developers" : ""}
              >
                {currentPage === "developers" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <Building
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "developers"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Developers
                  </span>
                )}
                {currentPage === "developers" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Highlights */}
          {canShowItem("Highlights", "Highlight", "sidebar") && (
            <li>
              <button
                onClick={() => onNavigate("highlights")}
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${currentPage === "highlights"
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                title={isCollapsed ? "Highlights" : ""}
              >
                {currentPage === "highlights" && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <Sparkles
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${currentPage === "highlights"
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Highlights
                  </span>
                )}
                {currentPage === "highlights" && !isCollapsed && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse"></div>
                )}
              </button>
            </li>
          )}

          {/* Divider */}
          <li className="py-2">
            <div className="border-t border-gray-100"></div>
          </li>

          {/* HRMS Dropdown */}
          {canShowItem("HRMS", "HRMS", "sidebar") && (
            <li>
              <button
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${[
                    "hrms-dashboard",
                    "employees",
                    "attendance",
                    "leave",
                    "interview",
                  ].includes(currentPage)
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                onClick={() => setIsHrmsOpen(!isHrmsOpen)}
                title={isCollapsed ? "HRMS" : ""}
              >
                {[
                  "hrms-dashboard",
                  "employees",
                  "attendance",
                  "leave",
                  "interview",
                ].includes(currentPage) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  )}
                <Users
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${[
                      "hrms-dashboard",
                      "employees",
                      "attendance",
                      "leave",
                      "interview",
                    ].includes(currentPage)
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    HRMS
                  </span>
                )}
                {!isCollapsed &&
                  (isHrmsOpen ? (
                    <ChevronUp
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ) : (
                    <ChevronDown
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ))}
                {[
                  "hrms-dashboard",
                  "employees",
                  "attendance",
                  "leave",
                  "interview",
                ].includes(currentPage) &&
                  !isCollapsed && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse absolute right-4"></div>
                  )}
              </button>
              {isHrmsOpen && (
                <ul
                  className={`${isCollapsed ? "space-y-1 mt-1" : "ml-4 space-y-0.5 mt-1"
                    }`}
                >
                  {auth?.role !== "agent" &&
                    canShowItem("HrmsDashboard", "HrmsDashboard", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("hrms-dashboard")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "hrms-dashboard"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Dashboard" : ""}
                      >
                        <LayoutGrid
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "hrms-dashboard"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Dashboard</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Users", "Users", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("employees")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "employees"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Users" : ""}
                      >
                        <Users
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "employees"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Users</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Attendance", "Attendance", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("attendance")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "attendance"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Attendance" : ""}
                      >
                        <ClipboardList
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "attendance"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Attendance</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Leaves", "Leaves", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("leave")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "leave"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Leaves" : ""}
                      >
                        <User
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "leave"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Leaves</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Interview", "Recruitment", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("interview")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "interview"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Interview" : ""}
                      >
                        <MessageSquare
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "interview"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Interview</span>
                        )}
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Payroll Dropdown */}
          {canShowItem("Payroll", "Payroll", "sidebar") && (
            <li>
              <button
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${[
                    "departments",
                    "shifts",
                    "off-days",
                    "allowances",
                    "public-holidays",
                    "salaries",
                  ].includes(currentPage)
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                onClick={() => setIsPayrollOpen(!isPayrollOpen)}
                title={isCollapsed ? "Payroll" : ""}
              >
                {[
                  "departments",
                  "shifts",
                  "off-days",
                  "allowances",
                  "public-holidays",
                  "salaries",
                ].includes(currentPage) && (
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  )}
                <CalendarCheck
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${[
                      "departments",
                      "shifts",
                      "off-days",
                      "allowances",
                      "public-holidays",
                      "salaries",
                    ].includes(currentPage)
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Payroll
                  </span>
                )}
                {!isCollapsed &&
                  (isPayrollOpen ? (
                    <ChevronUp
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ) : (
                    <ChevronDown
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ))}
                {[
                  "departments",
                  "shifts",
                  "off-days",
                  "allowances",
                  "public-holidays",
                  "salaries",
                ].includes(currentPage) &&
                  !isCollapsed && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse absolute right-4"></div>
                  )}
              </button>
              {isPayrollOpen && (
                <ul
                  className={`${isCollapsed ? "space-y-1 mt-1" : "ml-4 space-y-0.5 mt-1"
                    }`}
                >
                  {canShowItem("Departments", "Department", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("departments")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "departments"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Departments" : ""}
                      >
                        <Briefcase
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "departments"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Departments</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Shifts", "Shift", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("shifts")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "shifts"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Shifts" : ""}
                      >
                        <Clock
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "shifts"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Shifts</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Off Days", "OffDay", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("off-days")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "off-days"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Off Days" : ""}
                      >
                        <CalendarX
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "off-days"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Off Days</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Allowances", "Allowence", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("allowances")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "allowances"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Allowances" : ""}
                      >
                        <FileText
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "allowances"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Allowances</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Public Holidays", "Holiday", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("public-holidays")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "public-holidays"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Public Holidays" : ""}
                      >
                        <Home
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "public-holidays"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Public Holidays</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Salaries", "Salary", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("salaries")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "salaries"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Salaries" : ""}
                      >
                        <DollarSign
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "salaries"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Salaries</span>
                        )}
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Accounts Dropdown */}
          {canShowItem("Accounts", "Account", "sidebar") && (
            <li>
              <button
                className={`w-full flex items-center ${isCollapsed ? "justify-center px-3" : "gap-3 px-4"
                  } py-3.5 rounded-xl transition-all duration-200 cursor-pointer group relative overflow-hidden ${["expenses", "invoice", "incentive"].includes(currentPage)
                    ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white shadow-lg shadow-[#2e67be]/20"
                    : "text-gray-600 hover:bg-gradient-to-r hover:from-[#f9fbfd] hover:to-white hover:text-[#2e67be] hover:shadow-md active:scale-[0.98]"
                  }`}
                onClick={() => setIsAccountsOpen(!isAccountsOpen)}
                title={isCollapsed ? "Accounts" : ""}
              >
                {["expenses", "invoice", "incentive"].includes(currentPage) && (
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
                <TrendingUp
                  className={`w-5 h-5 transition-all duration-200 relative z-10 ${["expenses", "invoice", "incentive"].includes(currentPage)
                      ? "drop-shadow-sm"
                      : "group-hover:scale-110 group-hover:rotate-3"
                    }`}
                  strokeWidth={2.5}
                />
                {!isCollapsed && (
                  <span className="text-[15px] font-medium relative z-10">
                    Accounts
                  </span>
                )}
                {!isCollapsed &&
                  (isAccountsOpen ? (
                    <ChevronUp
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ) : (
                    <ChevronDown
                      className="w-4 h-4 ml-auto transition-all duration-200 relative z-10"
                      strokeWidth={2.5}
                    />
                  ))}
                {["expenses", "invoice", "incentive"].includes(currentPage) &&
                  !isCollapsed && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white/60 animate-pulse absolute right-4"></div>
                  )}
              </button>
              {isAccountsOpen && (
                <ul
                  className={`${isCollapsed ? "space-y-1 mt-1" : "ml-4 space-y-0.5 mt-1"
                    }`}
                >
                  {canShowItem("Expenses", "Expenses", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("expenses")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "expenses"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Expenses" : ""}
                      >
                        <Receipt
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "expenses"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Expenses</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Invoice", "Invoices", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("invoice")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "invoice"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Invoice" : ""}
                      >
                        <FileText
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "invoice"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Invoice</span>
                        )}
                      </button>
                    </li>
                  )}
                  {canShowItem("Incentive", "Loyalty", "sidebar") && (
                    <li>
                      <button
                        onClick={() => onNavigate("incentive")}
                        className={`w-full flex items-center ${isCollapsed
                            ? "justify-center px-2 py-2.5"
                            : "gap-3 px-4 py-3"
                          } rounded-lg transition-all duration-200 cursor-pointer group ${currentPage === "incentive"
                            ? "bg-[#4985f2] text-white"
                            : "text-gray-500 hover:bg-gray-50 hover:text-[#2e67be] active:bg-[#e8f1fc]"
                          }`}
                        title={isCollapsed ? "Incentive" : ""}
                      >
                        <Gift
                          className={`w-4 h-4 transition-transform duration-200 ${currentPage === "incentive"
                              ? ""
                              : "group-hover:scale-110"
                            }`}
                          strokeWidth={2.5}
                        />
                        {!isCollapsed && (
                          <span className="text-[14px]">Incentive</span>
                        )}
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>
    </aside>
  );
}
